import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { doc, onSnapshot, DocumentSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';

export interface RushdSettings {
  programActive: boolean;
  memberExpressPriceMode: 'percentage_off' | 'flat_price';
  memberRateValue: number;
  minOrderValueEnabled: boolean;
  minOrderValue: number;
  monthlyUsageLimitEnabled: boolean;
  monthlyUsageLimit: number;
  effectiveFrom?: Date;
  lastModifiedBy?: string;
  lastModifiedAt?: Date;
}

interface RushdSettingsContextType {
  settings: RushdSettings | null;
  loading: boolean;
  error: string | null;
}

const DEFAULT_SETTINGS: RushdSettings = {
  programActive: false,
  memberExpressPriceMode: 'percentage_off',
  memberRateValue: 25,
  minOrderValueEnabled: false,
  minOrderValue: 50,
  monthlyUsageLimitEnabled: false,
  monthlyUsageLimit: 10,
};

const RushdSettingsContext = createContext<RushdSettingsContextType | undefined>(undefined);

interface RushdSettingsProviderProps {
  children: ReactNode;
}

export function RushdSettingsProvider({ children }: RushdSettingsProviderProps) {
  const [settings, setSettings] = useState<RushdSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const settingsRef = doc(db, 'rushdSettings', 'main');

    const unsubscribe = onSnapshot(
      settingsRef,
      (snapshot: DocumentSnapshot) => {
        try {
          if (snapshot.exists()) {
            const data = snapshot.data();
            const settings: RushdSettings = {
              ...DEFAULT_SETTINGS,
              ...data,
              effectiveFrom: data?.effectiveFrom?.toDate(),
              lastModifiedAt: data?.lastModifiedAt?.toDate(),
            };
            setSettings(settings);
            setError(null);
          } else {
            // Document doesn't exist, use default settings
            setSettings(DEFAULT_SETTINGS);
            setError(null);
          }
        } catch (err) {
          console.error('Error processing Rush\'d settings:', err);
          setError('Failed to load Rush\'d settings');
          // Use cached settings or defaults
          if (!settings) {
            setSettings(DEFAULT_SETTINGS);
          }
        } finally {
          setLoading(false);
        }
      },
      (err) => {
        console.error('Error listening to Rush\'d settings:', err);
        setError('Failed to connect to Rush\'d settings');
        setLoading(false);
        // Use cached settings or defaults
        if (!settings) {
          setSettings(DEFAULT_SETTINGS);
        }
      }
    );

    return unsubscribe;
  }, []);

  const value: RushdSettingsContextType = {
    settings,
    loading,
    error,
  };

  return (
    <RushdSettingsContext.Provider value={value}>
      {children}
    </RushdSettingsContext.Provider>
  );
}

export function useRushdSettings() {
  const context = useContext(RushdSettingsContext);
  if (context === undefined) {
    throw new Error('useRushdSettings must be used within a RushdSettingsProvider');
  }
  return context;
}

// Utility function for backward compatibility with shipping service
export function getRushdPricingConfig(settings: RushdSettings | null) {
  if (!settings) {
    return {
      programActive: false,
      memberExpressPriceMode: 'percentage_off' as const,
      memberRateValue: 25,
    };
  }

  return {
    programActive: settings.programActive,
    memberExpressPriceMode: settings.memberExpressPriceMode,
    memberRateValue: settings.memberRateValue,
  };
}